import * as React from 'react';
import clsx from 'clsx';
import {
  styled,
  useThemeProps,
  type ComponentsProps,
  type ComponentsOverrides,
} from '@mui/material';
import useId from '@mui/utils/useId';
import { getToken } from '@workiva/unify-tokens';

import { ListItemSecondaryActions } from '../ListItemSecondaryActions';
import { ListItem, type ListItemProps } from '../ListItem';

import fileListItemClasses, {
  type FileListItemClassKey,
} from './fileListItemClasses';

import { FileListItemIcon } from './slots/FileListItemIcon';
import { FileListItemFileMeta } from './slots/FileListItemFileMeta';
import { FileListItemText } from './slots/FileListItemText';
import { FileListItemActionMenu } from './slots/FileListItemActionMenu';
import {
  defaultFileUploadPercentComplete,
  type FileModel,
  getFileStatusFromModel,
} from '../FileUploader/fileModel';
import type { FileUploaderProps } from '../FileUploader';

declare module '@mui/material/styles' {
  interface ComponentNameToClassKey {
    UnifyFileListItem: FileListItemClassKey;
  }

  interface ComponentsPropsList {
    UnifyFileListItem: FileListItemProps;
  }

  interface Components {
    UnifyFileListItem?: {
      defaultProps?: ComponentsProps['UnifyFileListItem'];
      styleOverrides?: ComponentsOverrides<Theme>['UnifyFileListItem'];
    };
  }
}

type FileListItemOwnProps = FileModel & {
  onRemove?: FileUploaderProps['onRemoveFileListItem'];
};

/**
 * The public prop API for the {@link FileListItem} component.
 */
export type FileListItemProps = Pick<
  ListItemProps,
  'id' | 'className' | 'component' | 'role' | 'sx'
> &
  Pick<
    FileUploaderProps,
    'showFileSize' | 'renderFileListItemActionMenuItems'
  > &
  FileListItemOwnProps;

const UnifyFileListItemRoot = styled(ListItem, {
  name: 'UnifyFileListItem',
  slot: 'Root',
  overridesResolver: (_, styles) => {
    return [
      styles.root,
      {
        [`&.${fileListItemClasses.fileStatusUploading}`]:
          styles.fileStatusUploading,
        [`&.${fileListItemClasses.fileStatusUploadError}`]:
          styles.fileStatusUploadError,
        [`&.${fileListItemClasses.fileStatusUploadSuccess}`]:
          styles.fileStatusUploadSuccess,
      },
    ];
  },
})(({ theme }) => ({
  border: `1px solid ${getToken('Core/Layer/layer-default-border')}`,
  borderRadius: theme.shape.borderRadius,

  [`.${fileListItemClasses.fileName}`]: {
    fontWeight: 'normal',
  },
}));

/**
 * A component that renders metadata about files, often uploaded by a {@link FileUploader}.
 *
 * TODO (FED-3994) Add example / use case description of standalone usage
 */
export const FileListItem = React.memo<FileListItemProps>(
  React.forwardRef<HTMLDivElement, FileListItemProps>(function FileListItem(
    inProps,
    forwardedRef
  ) {
    const props = useThemeProps({
      props: inProps,
      name: 'UnifyFileListItem',
    });
    const {
      id,
      className: classNameProp,
      component = 'div',
      file,
      errors,
      uploadPercentComplete = defaultFileUploadPercentComplete,
      showFileSize = false,
      renderFileListItemActionMenuItems,
      onRemove,
      ...rest
    } = props;

    // Reconstruct a fileModel object from spread props.
    // The props are spread on FileListItem for memoization purposes.
    const fileModel = {
      file,
      errors,
      uploadPercentComplete,
    } satisfies FileModel;

    const fileStatus = getFileStatusFromModel(fileModel);
    const metaElIsRendered = showFileSize || fileStatus === 'uploading';

    const rootId = useId(id);
    const metaElId = metaElIsRendered ? `${rootId}-meta` : undefined;
    const fileNameElId = `${rootId}-fileName`;
    const fileErrorElId = `${rootId}-fileError`;

    const className = clsx(
      classNameProp,
      fileListItemClasses.root,
      fileStatus === 'uploading' && fileListItemClasses.fileStatusUploading,
      fileStatus === 'error' && fileListItemClasses.fileStatusUploadError,
      fileStatus === 'done' && fileListItemClasses.fileStatusUploadSuccess
    );

    return (
      <UnifyFileListItemRoot
        id={rootId}
        // show full file name on hover
        title={file.name}
        className={className}
        // @ts-expect-error this shouldn't be an error as long as props.component is set to "div".
        ref={forwardedRef}
        aria-labelledby={fileNameElId}
        aria-invalid={fileStatus !== 'error' ? undefined : true}
        component={component}
        {...rest}
        secondaryActions={
          <ListItemSecondaryActions sx={{ pl: 1 }}>
            <FileListItemFileMeta
              id={metaElId}
              showFileSize={showFileSize}
              {...fileModel}
            />
            <FileListItemActionMenu
              file={file}
              fileIsUploading={fileStatus === 'uploading'}
              rootId={rootId}
              renderFileListItemActionMenuItems={
                renderFileListItemActionMenuItems
              }
              onRemove={onRemove}
            />
          </ListItemSecondaryActions>
        }
      >
        <FileListItemIcon
          fileStatus={fileStatus}
          fileType={file.type}
          uploadPercentComplete={uploadPercentComplete}
          className={fileListItemClasses.fileIcon}
          metaElId={metaElId}
        />

        <FileListItemText
          errors={errors ?? []}
          fileName={file.name}
          primaryTypographyProps={{
            id: fileNameElId,
          }}
          secondaryTypographyProps={{
            id: fileErrorElId,
          }}
        />
      </UnifyFileListItemRoot>
    );
  })
);

export default FileListItem;
